import { CommonModule } from '@angular/common';
import { Component, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { MonedaPipe } from 'app/pipes/moneda.pipe';
import { FormatOtherDateHourPipe } from 'app/pipes/other-date.pipe';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { RoutesFiltersComponent } from '../../filters/routes-filters/routes-filters.component';
import { getCode } from 'app/utils/method';
import { IPaginationResultFrequencies } from 'app/common/interfaces'
import { SubRouteTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/sub-route-type.enum';
import { subRoutesByGroupRequestDto, subRoutesFrecuencyResponseDto } from '../customer-route-frequency/dto/sub-route-reports.dto';
import { CustomerTerritoriesService } from './customer-territories.service';
import { ConfigurationFiltersComponent } from '../../filters/configuration-filters/configuration-filters.component';
import { ReportsRoutesExport } from '../../assistant/delivery/loads/exports/reports/reports-route.export';
import { MatDialog } from '@angular/material/dialog';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { ExportDialogComponent, ExportOptions } from 'app/layout/export-overlay';
import { CUSTOMER_TERRITORIES_EXPORT_ID, CUSTOMER_TERRITORIES_PROPERTIES, CUSTOMER_TERRITORIES_TITLE } from '../../assistant/delivery/loads/exports/reports/reports-route.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { Subscription } from 'rxjs';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { SmartGridComponent, ColDef, GridConfig, PageChangedEvent, SortChangedEvent } from 'app/shared/components/smart-grid';

@Component({
  selector: "app-customer-territories",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    SmartGridComponent,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    MonedaPipe,
    NgSelectComponent,
    MatTooltipModule,
    MatBadgeModule,
    FilterComponent,
    RoutesFiltersComponent,
    ConfigurationFiltersComponent,
    HoverMenuDirective,
    MatMenuModule,
  ],
  templateUrl: "./customer-territories.component.html",
  styleUrl: "./customer-territories.component.scss",
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    ReportsRoutesExport,
  ],
})
export class CustomerTerritoriesComponent implements OnInit, OnDestroy {
  getCode = getCode;

  data: subRoutesFrecuencyResponseDto[] = [];
  columnDefs: ColDef<subRoutesFrecuencyResponseDto>[] = [];
  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "total" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  totalItems: number = 0;
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  isLoading: boolean = false;

  form: FormGroup;
  searchSubscription: Subscription;

  loadFiltersData = false;

  /**
   *
   */
  constructor(
    private fb: FormBuilder,
    private customerTerritoriesService: CustomerTerritoriesService,
    private reportsRouteExport: ReportsRoutesExport,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private readonly searchService: SearchService
  ) {
    this.form = this.fb.group({
      routeId: [],
      routeTypeId: [],
      companyId: [],
    });

    this.initializeColumnDefs();
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        field: "routeName" as any,
        headerName: "Territorio",
        minWidth: 200,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.routeName || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.routeName || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "custmerCode" as any,
        headerName: "Código cliente",
        minWidth: 150,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.custmerCode || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.custmerCode || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "custmerName" as any,
        headerName: "Nombre cliente",
        minWidth: 250,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.custmerName || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.custmerName || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
      {
        field: "companyName" as any,
        headerName: "Compañía",
        minWidth: 200,
        sortable: true,
        valueFormatter: (value, data) => String(value || "-"),
        filterOptions: {
          valueExtractor: (item: any) => String(item.companyName || "-"),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.companyName || "-");
            if (!value || value === "-") return !filterValue;
            return value === filterValue;
          },
        },
      },
    ];
  }

  onFilterPanelOpened(): void {
    this.loadFiltersData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.findAllWithCount();
    }, 0);
  }

  onExportButtonPressed() {
    if (!this.reportsRouteExport.canStartTask(CUSTOMER_TERRITORIES_EXPORT_ID))
      return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: CUSTOMER_TERRITORIES_TITLE },
    });

    let dataToFilter: subRoutesByGroupRequestDto = {
      search: this.search,
      page: this.pageIndex,
      limit: this.pageSize,
      companyId: this.form.value.companyId,
      type: this.form.value.routeTypeId,
      routesIds: this.form.value.routeId ? this.form.value.routeId : [],
      routeGroupsIds: [],
    };

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.reportsRouteExport.exportCustomerTerritories(
          options.format,
          options.range,
          CUSTOMER_TERRITORIES_EXPORT_ID,
          CUSTOMER_TERRITORIES_TITLE,
          this.totalItems,
          dataToFilter,
          CUSTOMER_TERRITORIES_PROPERTIES
        );
      }
    });
  }

  onSortChange(event: SortChangedEvent<subRoutesFrecuencyResponseDto>): void {
    this.pageIndex = 1;
    this.findAllWithCount();
  }

  onClearFilter() {
    this.form.reset();
    this.findAllWithCount();
  }

  onPageChange(event: PageChangedEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex + 1;
    this.findAllWithCount();
  }

  findAllWithCount(): void {
    let dataToFilter: subRoutesByGroupRequestDto = {
      search: this.search,
      page: this.pageIndex,
      limit: this.pageSize,
      companyId: this.form.value.companyId,
      type: this.form.value.routeTypeId,
      routesIds: this.form.value.routeId ? this.form.value.routeId : [],
      routeGroupsIds: [],
    };

    this.isLoading = true;
    this.customerTerritoriesService.findAllWithCount(dataToFilter).subscribe({
      next: (
        res: IPaginationResultFrequencies<subRoutesFrecuencyResponseDto>
      ) => {
        this.data = res?.data?.data ?? [];
        this.totalItems = res?.data?.pageInfo?.[0]?.total ?? 0;
        this.isLoading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener información del reporte.",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(
      (term) => {
        this.pageIndex = 1;
        this.search = term;
        this.findAllWithCount();
      }
    );
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }
}
